import { useEffect, useState } from 'react'
import { client } from '@common/config/api/client'
import type { TimepointSubmissionUploader } from '@common/types/uploader'

interface QcUploaderParams {
  study_id?: string
  qc_task_id?: string
}

/**
 * Read-only lookup of the user who uploaded/submitted the timepoint data (QC Tasks flow).
 * Only fetches when `enabled` is true (e.g. when the details popup is opened).
 */
export const useGetQcTaskUploader = (params: QcUploaderParams, enabled: boolean) => {
  const [data, setData] = useState<TimepointSubmissionUploader>()
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  useEffect(() => {
    const canFetch = enabled && !!params.study_id && !!params.qc_task_id

    if (!canFetch) {
      return
    }

    let isCurrentRequest = true
    setIsLoading(true)
    setIsError(false)

    client
      .get<TimepointSubmissionUploader, unknown, true>({
        url: '/qc_tasks/{qc_task_id}/uploader',
        path: {
          qc_task_id: params.qc_task_id!,
        },
        query: {
          study_id: params.study_id!,
        },
        throwOnError: true,
      })
      .then((response) => {
        if (isCurrentRequest) {
          setData(response.data as TimepointSubmissionUploader)
        }
      })
      .catch(() => {
        if (isCurrentRequest) {
          setIsError(true)
        }
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsLoading(false)
        }
      })

    return () => {
      isCurrentRequest = false
    }
  }, [enabled, params.study_id, params.qc_task_id])

  return { data, isLoading, isError }
}
